export type Project = {
  id: string
  index: string
  kind: string
  title: string
  subtitle: string
  brandNote?: string
  disclaimer: string
  image: string
  imageAlt: string
  color: 'blue' | 'red' | 'green'
  brief: string
  audience: string
  insight: string
  strategy: string
  concept: string
  executions: Array<{ title: string; detail: string }>
  kpis: string[]
  contribution: string[]
  interactive?: boolean
  validation?: Array<{ action: string; behavior: string; signal: string }>
  tradeoffs?: string[]
  risk?: { title: string; response: string }
  caseLogic?: {
    businessQuestion: string
    objective: string
    route: Array<{ label: string; detail: string }>
    outputs: Array<{ name: string; detail: string }>
    workplan: Array<{ phase: string; action: string; handoff: string }>
  }
}

export const profile = {
  name: '你的名字',
  role: '品牌整合营销策划｜求职作品集',
  tagline: '把洞察变成\n有感觉的行动',
  location: '所在城市待补充',
  education: '教育背景待补充',
  email: '',
  photo: '',
  resumeUrl: '',
  bio: '我喜欢从日常里捡问题：人们为什么停下来、为什么愿意分享、又为什么在某个瞬间相信一个品牌。这里收录了三个自主命题项目，展示我如何把观察整理成策略，再把策略变成能被看见、参与和记住的体验。',
}

export const capabilities = [
  ['01', '消费者洞察', '从生活语境、情绪张力与行为矛盾中，找到值得品牌回应的问题。'],
  ['02', '整合传播', '让产品、内容、社交与线下触点围绕同一个核心概念协同发生。'],
  ['03', '创意表达', '把策略翻译成一句可记住的话、一套可参与的机制和一组可落地的内容。'],
  ['04', '项目推进', '拆解节奏、角色和交付物，在想法与现实之间保持清醒的来回。'],
]

export const tools = ['策略拆解', '内容策划', '用户研究', '活动策划', '传播节奏', '数据复盘', 'PPT / Keynote', 'Excel', 'AI 协作']

export const projects: Project[] = [
  {
    id: 'after-eight',
    index: '01',
    kind: '真实品牌概念提案',
    title: '观夏｜晚八点以后',
    subtitle: '把一天还给自己，从回到家的那一刻开始。',
    brandNote: '观夏是一个以东方文化与植物气息为灵感的香氛品牌，用当代产品唤起人们熟悉的东方记忆。',
    disclaimer: '非官方自主概念提案，与观夏品牌无商业合作关系',
    image: '/images/after-eight.jpg',
    imageAlt: '深蓝夜色窗景、香水瓶与外套组成的手绘纸张拼贴',
    color: 'blue',
    brief: '为观夏构思一次面向都市青年的夏末限定传播：不再继续讲“远方与自然”，而是回到每天最小也最真实的松弛时刻——晚八点以后，终于回到自己的空间。',
    audience: '24–32 岁一二线城市青年；白天在规则中高效运转，夜晚通过气味、音乐和独处重新确认自我。',
    insight: '真正稀缺的不是休假，而是下班后不再扮演任何角色的两小时。香气可以成为从“工作中的我”切换回“我自己”的情绪开关。',
    strategy: '将限定香气定义为一份“下班仪式”，用一个明确时间点串联产品命名、内容故事、城市媒介和用户参与。品牌不教人如何松弛，只替那个瞬间留一盏灯。',
    concept: '晚八点以后｜今晚不再回复世界',
    executions: [
      { title: '一支夜归短片', detail: '记录三个普通人的回家动作：脱鞋、关通知、打开窗。结尾只留下时间与一声轻轻的门响。' },
      { title: '8:00 城市灯箱', detail: '写字楼与地铁出口灯箱在晚八点切换为“今天到这里就好”，把媒介本身变成下班提示。' },
      { title: '气味留言处', detail: '门店设置夜间气味卡与匿名留言，把“今晚想放下的事”折起投入纸箱，换取一张晚安试香纸。' },
      { title: '七夜内容计划', detail: '联合音乐、家居与独处型创作者，围绕七种回家仪式生成可收藏的短内容，而非密集种草。' },
    ],
    kpis: ['限定香气预约转化率', '20:00 后门店到访占比', '用户留言与 UGC 数量', '品牌关键词情绪联想变化'],
    contribution: ['命题设定与人群洞察', '传播概念与内容主线', '线上线下触点设计', 'KPI 框架与节奏规划'],
    interactive: true,
    caseLogic: {
      businessQuestion: '观夏已经拥有鲜明的东方诗意，夏末限定如何不重复“远方与自然”，同时让都市青年产生一个具体的试闻理由？',
      objective: '为限定香气建立“晚八点”这一可记忆、可重复的使用时刻，并把情绪认同逐步导向主题访问、到店试闻与预约。',
      route: [
        { label: '品牌语境', detail: '保留克制、诗意与嗅觉叙事，不另造一套陌生语气。' },
        { label: '人群洞察', detail: '白天持续回应他人，夜晚才重新取得时间与空间的控制权。' },
        { label: '核心策略', detail: '用每天都会发生的 20:00，替香气定义一个明确使用场景。' },
        { label: '转化路径', detail: '内容共鸣 → 收藏主题 → 到店试闻与留言 → 预约或带走试香。' },
      ],
      outputs: [
        { name: '策略', detail: '一页式 Brief、核心命题与人群洞察' },
        { name: '内容', detail: '15 秒主片脚本、七夜选题表、创作者 Brief' },
        { name: '媒介', detail: '20:00 灯箱文案与城市触点节奏表' },
        { name: '零售', detail: '气味留言 SOP、试香卡文案与店员话术' },
        { name: '复盘', detail: '行为信号看板与阶段复盘问题清单' },
      ],
      workplan: [
        { phase: 'W0｜对齐', action: '确认产品卖点、库存、重点门店、预算区间与审批边界，反问并锁定 Brief。', handoff: '一页式策略 Brief · RACI 分工表' },
        { phase: 'W1｜验证', action: '补充竞品语境扫描，并计划访谈 6–8 名目标用户，验证“晚八点”是不是共有且可行动的时刻。', handoff: '洞察证据卡 · 核心假设清单' },
        { phase: 'W2｜搭建', action: '把核心概念拆成内容、媒介、门店三条任务流，定义每一条的下一步行为。', handoff: '传播路径图 · 交付物清单' },
        { phase: 'W3–4｜制作', action: '协同创意、媒介、零售与供应方推进脚本、物料打样、排期和门店培训。', handoff: '制作排期 · 门店 SOP · 上线检查表' },
        { phase: '上线后｜复盘', action: '按认知、参与、试闻、预约四层查看信号，定位掉点并调整内容或现场承接。', handoff: '数据看板 · 周复盘与优化动作' },
      ],
    },
    validation: [
      { action: '一支夜归短片', behavior: '看完后把“晚八点”与自己的回家动作联系起来，并主动搜索主题', signal: '完整播放率、主题页访问来源、品牌词与“下班 / 回家”组合搜索增量' },
      { action: '20:00 城市灯箱', behavior: '在正确时间被提醒停一下，进入主题页或查看附近门店', signal: '20:00–22:00 扫码访问、门店路线点击、限定香气预约行为' },
      { action: '气味留言处', behavior: '到店试闻、写下一件想放下的事，并愿意带走或分享试香纸', signal: '留言完成率、试香领取率、参与者自然发布与二次到店意向' },
      { action: '七夜内容计划', behavior: '收藏一种可复用的下班仪式，而不是看完一次种草内容就离开', signal: '收藏 / 分享比、系列回访率、“晚八点以后”自然提及率' },
    ],
    tradeoffs: [
      '不再重复宽泛的“东方自然”，而是选择每天都会发生的下班时刻，让香气拥有明确使用场景。',
      '不把达人数量当作传播重点，优先经营可连续七天发生的仪式和用户参与。',
      '不靠折扣制造首发爆点，先用城市触点与门店体验建立情绪价值，再承接预约与购买。',
    ],
    risk: {
      title: '情绪概念容易被记住，但产品本身可能被忘掉',
      response: '每个情绪触点都绑定一次具体的试闻、预约或门店动作；复盘时同时观察主题记忆和限定香气识别，避免“只记得文案”。',
    },
  },
  {
    id: 'unfinished',
    index: '02',
    kind: '虚构品牌｜新品上市',
    title: '未满｜皮肤不用满分',
    subtitle: '一套不催你变得更好的基础护肤。',
    disclaimer: '自主命题 · 虚构品牌 · 所有产品与数据均为概念设定',
    image: '/images/unfinished.jpg',
    imageAlt: '无标识护肤软管、不规则手镜与彩色蜡笔线条组成的纸艺拼贴',
    color: 'red',
    brief: '从零建立一个面向年轻人的基础护肤品牌，并完成首套“留白三件组”上市。挑战是：在功效语言高度同质化的市场里，既反对完美焦虑，又不能落入只谈情绪、不谈产品的空洞表达。',
    audience: '20–28 岁轻度护肤人群；懂成分但不想被复杂流程绑架，会为真实、轻松和审美一致的品牌买单。',
    insight: '消费者厌倦的不是功效，而是“必须持续修正自己”的压迫感。基础护肤的价值，可以从追求满分转向维持皮肤舒服、生活不被打断。',
    strategy: '以“够用主义”重写产品价值：配方说清楚、步骤做减法、沟通不制造新的缺陷。用真实状态替代前后对比，把不完美变成品牌可持续的内容资产。',
    concept: '未满，也很好｜皮肤不用参加考试',
    executions: [
      { title: '留白三件组', detail: '洁面、精华乳、修护霜只保留必要步骤；包装用未涂满的色块区分肤感与使用时段。' },
      { title: '不打分试用计划', detail: '测评不要求晒前后对比，只记录七天里“皮肤没有打扰我的时刻”，把体验从外观转向感受。' },
      { title: '今天几分都可以', detail: '社交内容邀请用户上传一张未经修饰的生活局部；品牌只回应一句具体而不评判的话。' },
      { title: '成分小抄', detail: '用一张可以撕下来的纸解释核心配方、适用边界和不建议搭配，建立克制而可信的专业感。' },
    ],
    kpis: ['首发套组加购率', '试用完成与反馈率', '内容收藏 / 评论比', '“简单、诚实、舒服”心智占比'],
    contribution: ['品牌定位与命名', '新品组合与上市主题', '种草机制与内容栏目', '包装沟通与指标设计'],
    interactive: true,
    caseLogic: {
      businessQuestion: '基础护肤的功效语言高度同质化，如何反对“皮肤必须满分”的焦虑，同时建立可信的产品理由与首发转化？',
      objective: '建立“够用主义”的差异化定位，让目标人群从价值认同进入产品理解、七天试用与首发套组购买。',
      route: [
        { label: '市场诊断', detail: '成分与功效承诺越来越密集，但基础护肤缺少轻松、可信且不过度教育的表达。' },
        { label: '人群洞察', detail: '用户并不排斥功效，而是厌倦持续发现问题、修正自己的消费压力。' },
        { label: '核心策略', detail: '用“够用主义”同时约束产品步骤、功效话术、包装信息和内容机制。' },
        { label: '上市路径', detail: '价值共鸣 → 产品理解 → 七天不打分试用 → 套组购买与真实反馈。' },
      ],
      outputs: [
        { name: '品牌策略', detail: '定位金字塔、Message House 与品牌语气' },
        { name: '产品沟通', detail: '卖点优先级、成分 FAQ 与适用边界' },
        { name: '内容种草', detail: '首发主视觉、七天任务与创作者 Brief' },
        { name: '渠道承接', detail: '试用机制、详情页结构与首发节奏表' },
        { name: '复盘', detail: '认知—试用—购买漏斗与反馈标签表' },
      ],
      workplan: [
        { phase: 'W0｜产品对齐', action: '确认配方、功效证据、宣称边界、成本与首批库存，锁定不能被创意改写的产品事实。', handoff: '产品信息底表 · 合规问题清单' },
        { phase: 'W1｜机会验证', action: '完成品类表达扫描，并计划访谈 8–10 名轻度护肤用户，验证“够用”是否带来信任而非低效感。', handoff: '竞品语境图 · 人群洞察卡' },
        { phase: 'W2｜定位搭建', action: '完成品牌定位、产品角色和首发概念，将情绪价值逐条接回成分、肤感与使用步骤。', handoff: 'Message House · 产品卖点排序' },
        { phase: 'W3｜内容与试用', action: '设计七天不打分试用、创作者内容框架、详情页与反馈题目，小样测试理解度。', handoff: '内容 Brief · 试用 SOP · 页面线框' },
        { phase: 'W4｜上市协同', action: '协同产品、电商、设计、媒介与客服完成物料、库存、排期、FAQ 和上线检查。', handoff: '上市作战表 · 渠道物料清单' },
        { phase: '上线后｜复盘', action: '按价值认同、产品理解、试用完成和购买转化分层查看掉点，调整内容与详情页信息。', handoff: '漏斗看板 · 用户反馈标签表' },
      ],
    },
    validation: [
      { action: '不打分试用计划', behavior: '完成七天基础护理，并用“有没有打扰生活”描述体验', signal: '试用完成率、有效体验记录率、肤感与生活场景关键词' },
      { action: '成分小抄', behavior: '快速理解核心成分、适用边界与三步使用方式', signal: '内容收藏率、详情页停留与 FAQ 重复咨询下降' },
      { action: '真实状态内容', behavior: '愿意保存、评论或分享未经修饰但不贩卖焦虑的内容', signal: '收藏 / 评论比、自然互动语义与品牌价值词提及' },
      { action: '留白三件组', behavior: '从单品理解进入套组加购，并保持步骤简单的产品认知', signal: '套组加购率、试用后购买意向、三件产品角色识别率' },
    ],
    tradeoffs: [
      '不把“反焦虑”做成纯态度口号，每一条情绪表达都必须接回产品事实与适用边界。',
      '不追求复杂的首发 SKU，用三件组建立清晰步骤，降低第一次购买的理解成本。',
      '不使用夸张前后对比，以连续使用感受和生活未被打断的时刻替代单一视觉结果。',
    ],
    risk: {
      title: '“不用满分”可能被误解为产品功效不够强',
      response: '把“够用”定义为配方透明、步骤必要、边界清楚，而不是降低功效标准；首发内容同时检查价值认同和产品卖点理解。',
    },
  },
  {
    id: 'useless-weekend',
    index: '03',
    kind: '虚构项目｜事件营销',
    title: '无用周末节',
    subtitle: '来浪费一点时间，带走一点自己。',
    disclaimer: '自主命题 · 虚构城市生活方式活动',
    image: '/images/useless-weekend.jpg',
    imageAlt: '城市公园里的条纹天幕、野餐垫、软椅、收音机与风筝手绘场景',
    color: 'green',
    brief: '策划一场可被生活方式品牌共同参与的城市周末事件。它不以演出阵容制造短期流量，而以一组低门槛、无结果要求的体验，回应年轻人的效率疲惫。',
    audience: '22–35 岁城市青年；周末也被健身、学习、打卡与社交安排填满，想休息，却总觉得“什么都没做”是一种浪费。',
    insight: '休息之所以困难，是因为它仍被要求产生成果。真正的松弛需要一个被共同认可的借口：今天做的事可以没有用，也不需要拍得好看。',
    strategy: '把“无用”设计成可参与的体验规则，而不只是一句口号。通过任务、物品和品牌三种介入，让参与者在现场主动放下效率，同时为合作品牌留下自然的产品角色。',
    concept: '无用周末节｜不赶进度，只赶草地',
    executions: [
      { title: '没结果工作坊', detail: '拼贴、捏土、涂鸦都不设置范例和成品展示墙；作品可以带走，也可以当场拆掉。' },
      { title: '无目的任务签', detail: '入场随机抽取一件可以不完成的小事，例如“沿着影子走三分钟”或“替陌生人画一朵云”；不设核验，也不与奖励绑定。' },
      { title: '品牌无用商店', detail: '合作品牌不设硬广摊位，只提供与休息有关的物件：一块坐垫、一杯慢慢喝的饮品、一页空白纸。' },
      { title: '空白节目单', detail: '每天只公布一半安排，另一半由现场天气、参与者提议和随机抽签共同决定。' },
    ],
    kpis: ['预约到场率与停留时长', '体验项目完成率', '合作品牌自然提及率', '活动后“愿意再次参加”比例'],
    contribution: ['活动命题与体验策略', '空间内容与参与机制', '品牌合作权益设计', '传播节奏与评估框架'],
    interactive: true,
    caseLogic: {
      businessQuestion: '如何策划一场不依赖明星阵容的城市生活方式事件，既让参与者愿意停留，也让合作品牌获得自然、可衡量的现场角色？',
      objective: '以“无结果要求”的体验规则建立事件辨识度，并形成预约、到场、停留、参与和品牌互动的完整路径。',
      route: [
        { label: '事件机会', detail: '多数城市活动用阵容和打卡制造流量，却很少真正缓解周末仍需完成任务的疲惫。' },
        { label: '人群洞察', detail: '用户需要的不是更多安排，而是一个被共同允许“什么都不产出”的公共理由。' },
        { label: '核心策略', detail: '把“无用”写成现场规则，通过任务、物品和品牌三种介入让松弛真实发生。' },
        { label: '参与路径', detail: '预约选时 → 抽取无目的任务 → 自由体验 → 品牌补给 → 带走一段无目的记忆。' },
      ],
      outputs: [
        { name: '事件策略', detail: '活动 Brief、主题规则与目标人群路径' },
        { name: '空间内容', detail: '分区图、节目表、容量与动线设计' },
        { name: '现场执行', detail: '体验 SOP、人员排班与应急预案' },
        { name: '品牌合作', detail: '权益包、品类角色与露出边界' },
        { name: '传播复盘', detail: '招募节奏、报名漏斗与现场数据表' },
      ],
      workplan: [
        { phase: 'W0｜约束对齐', action: '确认场地、日期、容量、预算、天气预案、安全要求和可合作品类，先锁定不可突破的边界。', handoff: '项目 Brief · 风险与限制清单' },
        { phase: 'W1｜体验验证', action: '访谈目标参与者并进行轻量概念测试，确认“无用”是被理解为松弛，而不是无聊或组织混乱。', handoff: '概念测试卡 · 核心体验原则' },
        { phase: 'W2｜空间与机制', action: '完成分区、容量、排队、物料、参与步骤与品牌角色设计，逐点检查体验和商业目标。', handoff: '场地图 · 用户旅程 · 权益包' },
        { phase: 'W3–4｜制作招募', action: '推进供应商、志愿者、品牌物料、报名页面、传播内容和现场演练。', handoff: '总排期 · 物料表 · 培训手册' },
        { phase: '活动日｜指挥', action: '按时段监控入场、容量、排队、物资和天气，设置明确的升级与决策责任人。', handoff: '现场 Run Sheet · 异常记录表' },
        { phase: '活动后｜复盘', action: '合并报名、核销、停留、体验和品牌互动数据，区分热闹程度与真实参与质量。', handoff: '结案报告 · 合作品牌复盘页' },
      ],
    },
    validation: [
      { action: '无目的任务签', behavior: '抽取后尝试完成或主动放弃，再自然进入其他现场体验', signal: '抽签参与率、任务签带走／回贴比例、后续体验导流率' },
      { action: '没结果工作坊', behavior: '进入体验、停留并允许作品不被展示或评价', signal: '分时段参与率、平均停留、完成与主动拆除比例' },
      { action: '品牌无用商店', behavior: '自然使用品牌提供的休息物件，而非只领取赠品', signal: '物件使用率、体验后品牌提及与品类角色识别' },
      { action: '空白节目单', behavior: '因随机内容延长停留，并愿意参与临时生成的活动', signal: '跨时段留存、随机节目参与率、再次参加意愿' },
    ],
    tradeoffs: [
      '不以舞台阵容做流量中心，把预算优先放在体验容量、舒适度与现场运营。',
      '品牌不设传统硬广摊位，只能提供与休息动作直接相关的物品或服务。',
      '保留随机性，但容量、安全、天气和人员调度必须通过后台规则保持可控。',
    ],
    risk: {
      title: '“无用”如果缺少规则，容易变成排队、无聊或现场失控',
      response: '前台保留松弛与随机，后台明确容量、时段、动线和异常处理；用停留质量与体验完成率判断活动，而不是只看人流。',
    },
  },
]
